/**
 * The insurance estimate builder.
 *
 * Xactimate's page, made typeable. Same seven columns in the same order, the
 * same areas, the same continuous line numbering, and a summary that ends on
 * Net Claim — so a rep who has spent years reading carrier estimates already
 * knows where everything is.
 *
 * Jonathon, 2026-08-24: "we need to be able to add line items that mirror the
 * same look but they'll be custom line items we can type into."
 *
 * ## Three things this screen does not do
 *
 * **It does not touch the job's estimate.** Not one query here reads or writes
 * `estimates`, `estimate_items` or anything the totals ladder sees. The claim
 * sheet is a second document (ADR 0055), and an adjuster's negotiating position
 * is not the company's profit.
 *
 * **It does no arithmetic.** Every figure on screen comes from
 * `computeInsuranceEstimate` in packages/core, which is pinned to two real
 * Xactimate estimates. CLAUDE.md rule 3: no money maths in a component.
 *
 * **It has no save button.** Fields write on blur, like the rest of the app. A
 * rep pricing a roof off an adjuster's printout is typing continuously and
 * should never be deciding whether their work is safe.
 *
 * ## Why the library exists
 *
 * The same forty lines appear on nearly every claim, with the same long
 * Xactimate descriptions. Typing "R&R Hip / Ridge cap - cut from 3 tab -
 * composition shingles" by hand once is fine; doing it on every job is how a
 * tool stops being used. Every line typed here is remembered, and the picker
 * offers it back with its prices already filled in.
 */

import {
  INSURANCE_ROOF_TYPES,
  INSURANCE_UNITS,
  computeInsuranceEstimate,
  formatCents,
  jobAddress,
  roofTypeHasTemplate,
  roofTypeLabel,
  type Cents,
  type InsuranceAreaInput,
  type Job,
} from "@roof-hawk/core";
import { useMemo, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { fmtDate, toLocalDateInput } from "../../lib/format";
import { useAsync } from "../../lib/hooks";
import {
  addInsuranceArea,
  addInsuranceItem,
  applyRoofTemplate,
  fetchClaimBranding,
  fetchInsuranceEstimate,
  fetchInsuranceLibrary,
  rememberInsuranceLine,
  startInsuranceEstimate,
  type InsuranceItemRow,
  type InsuranceLibraryRow,
} from "../../lib/insuranceEstimates";
import {
  insuranceBranding,
  printInsuranceEstimate,
} from "../../lib/printInsuranceEstimate";
import { deleteRow, updateRow } from "../../lib/queries";

const money = (value: Cents): string => formatCents(value);

/** A number as it should sit in an input: blank rather than a stubborn 0. */
const inputValue = (value: number | string | null): string =>
  value === null || value === undefined ? "" : String(value);

export function InsuranceEstimateCard({ job }: { job: Job }) {
  const bundle = useAsync(() => fetchInsuranceEstimate(job.id), [job.id]);
  const branding = useAsync(() => fetchClaimBranding(), []);
  const library = useAsync(() => fetchInsuranceLibrary(), []);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** What just happened, for an action whose result is off the bottom of the screen. */
  const [note, setNote] = useState<string | null>(null);

  const data = bundle.data;

  /**
   * Everything on screen, priced — with each stored row paired to the figures
   * computed for it.
   *
   * Zipped here rather than by index in the markup, so the table cannot render
   * one line's quantity beside another's total. Recomputed on every change
   * rather than stored, which is what makes editing the tax rate move every
   * line at once.
   */
  const view = useMemo(() => {
    if (!data) return null;

    const grouped = data.areas.map((area) => ({
      area,
      rows: data.items.filter((item) => item.area_id === area.id),
    }));

    const totals = computeInsuranceEstimate(
      grouped.map(({ area, rows }): InsuranceAreaInput => ({
        name: area.name,
        items: rows.map((item) => ({
          description: item.description,
          note: item.note,
          qty: item.qty,
          unit: item.unit,
          removePrice: item.remove_price,
          replacePrice: item.replace_price,
          materialPct: item.material_pct,
        })),
      })),
      {
        taxRate: data.estimate.tax_rate,
        defaultMaterialPct: data.estimate.default_material_pct,
        overheadPct: data.estimate.overhead_pct,
        profitPct: data.estimate.profit_pct,
        applyOverheadAndProfit: data.estimate.apply_overhead_and_profit,
      },
    );

    const areas = grouped.flatMap(({ area, rows }, index) => {
      const priced = totals.areas[index];
      if (!priced) return [];
      const lines = rows.flatMap((row, i) => {
        const line = priced.items[i];
        return line ? [{ row, line }] : [];
      });
      return [{ area, rows, priced, lines }];
    });

    return { totals, areas };
  }, [data]);

  async function guard(work: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await work();
      await bundle.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  /** Patch one column on the estimate — tax rate, O&P, the claim details. */
  const patchEstimate = (patch: Record<string, unknown>) =>
    guard(() => updateRow("insurance_estimates", data!.estimate.id, patch));

  /**
   * Patch one column on a line, and let the library learn it.
   *
   * The library write is best-effort on purpose: a rep who has just typed a
   * price must not see an error because a lookup table would not take it.
   */
  async function patchItem(
    item: InsuranceItemRow,
    patch: Partial<InsuranceItemRow>,
  ): Promise<void> {
    await guard(async () => {
      await updateRow("insurance_estimate_items", item.id, patch);
      const merged = { ...item, ...patch };
      if (merged.description.trim()) {
        try {
          await rememberInsuranceLine(merged);
          await library.reload();
        } catch {
          /* the line is saved; remembering it is a convenience */
        }
      }
    });
  }

  /**
   * Change the roof the claim is about, and offer its scope.
   *
   * On an estimate with nothing on it yet the lines go straight in — that is
   * the whole point of the dropdown, and there is nothing to disturb. Once a
   * rep has typed something, the type is recorded and the lines are left to a
   * button, because silently appending fourteen rows underneath somebody's
   * work is not what picking from a dropdown should do.
   */
  async function chooseRoofType(roofType: string): Promise<void> {
    const empty = data !== null && data.items.length === 0;
    const firstArea = data?.areas[0];

    await guard(async () => {
      await updateRow("insurance_estimates", data!.estimate.id, {
        roof_type: roofType,
      });
      if (empty && firstArea && roofTypeHasTemplate(roofType)) {
        await applyRoofTemplate(firstArea.id, roofType);
      }
    });
  }

  if (job.bid_type !== "insurance") {
    return (
      <div className="block">
        <h2>Insurance Estimate</h2>
        <div className="stub">
          <b>This is a {job.bid_type} job</b>
          The claim estimate is for insurance jobs. Change the bid type on the
          Overview tab and it appears here.
        </div>
      </div>
    );
  }

  if (bundle.loading && !data) return <div className="block" />;

  if (!data) {
    return (
      <div className="block">
        <h2>Insurance Estimate</h2>
        <ErrorNote error={bundle.error ?? error} />
        <div className="stub">
          <b>No claim estimate yet</b>
          Build the estimate the way the adjuster reads it — areas, line items,
          remove and replace prices, tax and O&P — then print it for the
          carrier. It is a separate document: nothing here changes the job's own
          estimate, its profit or anybody's commission.
        </div>
        <button
          className="btn-primary"
          disabled={busy}
          onClick={() => void guard(() => startInsuranceEstimate(job.id))}
        >
          {busy ? "Starting…" : "Start claim estimate"}
        </button>
      </div>
    );
  }

  const { estimate, areas } = data;
  const nextSort = (existing: { sort_order: number }[]) =>
    existing.reduce((max, row) => Math.max(max, row.sort_order), 0) + 10;

  return (
    <div className="block wide-card">
      <div className="insure-head">
        <h2>Insurance Estimate</h2>
        <button
          className="btn-primary"
          disabled={!view}
          title="Open the printable claim sheet — use your browser's Save as PDF"
          onClick={() => {
            if (!view) return;
            printInsuranceEstimate(
              view.totals,
              insuranceBranding(
                branding.data?.settings ?? null,
                branding.data?.contact ?? null,
                window.location.origin,
              ),
              {
                title: estimate.name,
                insuredName: job.contact?.full_name ?? null,
                propertyAddress: jobAddress(job) || null,
                carrier: estimate.carrier,
                claimNumber: estimate.claim_number,
                dateOfLoss: estimate.date_of_loss
                  ? fmtDate(estimate.date_of_loss)
                  : null,
                estimateDate: fmtDate(new Date().toISOString()),
              },
            );
          }}
        >
          Print for the carrier
        </button>
      </div>

      <ErrorNote error={bundle.error ?? error} />

      {/* ------------------------------------------------- the claim ---- */}
      <div className="insure-fields">
        <label className="field">
          <span>Document title</span>
          <input
            defaultValue={estimate.name}
            onBlur={(e) =>
              e.target.value !== estimate.name &&
              void patchEstimate({ name: e.target.value })
            }
          />
        </label>
        <label className="field">
          <span>Carrier</span>
          <input
            defaultValue={estimate.carrier ?? ""}
            placeholder="State Farm"
            onBlur={(e) =>
              e.target.value !== (estimate.carrier ?? "") &&
              void patchEstimate({ carrier: e.target.value || null })
            }
          />
        </label>
        <label className="field">
          <span>Claim number</span>
          <input
            defaultValue={estimate.claim_number ?? ""}
            onBlur={(e) =>
              e.target.value !== (estimate.claim_number ?? "") &&
              void patchEstimate({ claim_number: e.target.value || null })
            }
          />
        </label>
        <label className="field">
          <span>Date of loss</span>
          <input
            type="date"
            defaultValue={toLocalDateInput(estimate.date_of_loss)}
            onBlur={(e) =>
              void patchEstimate({ date_of_loss: e.target.value || null })
            }
          />
        </label>
      </div>

      {/* --------------------------------------------- how it is priced ---- */}
      <div className="insure-fields">
        <label className="field">
          <span>Roof type</span>
          <select
            value={estimate.roof_type}
            onChange={(e) => void chooseRoofType(e.target.value)}
          >
            {INSURANCE_ROOF_TYPES.map((type) => (
              <option key={type.code} value={type.code}>
                {type.label}
              </option>
            ))}
          </select>
          <i className="na-hint">
            {roofTypeHasTemplate(estimate.roof_type)
              ? "Its usual lines, at your prices"
              : "Type your own lines"}
          </i>
        </label>
        <label className="field">
          <span>
            Sales tax %
            <i
              className="na-hint"
              title="Applied to the material share of each line, the way Xactimate taxes materials only"
            >
              {" "}
              ⓘ
            </i>
          </span>
          <input
            type="number"
            step="0.001"
            defaultValue={inputValue(estimate.tax_rate)}
            onBlur={(e) =>
              void patchEstimate({ tax_rate: e.target.value || 0 })
            }
          />
        </label>
        <label className="field">
          <span>
            Default material %
            <i
              className="na-hint"
              title="What share of a line is taxable material when the line does not say. Nudge a single line's own % to match a carrier's figure exactly."
            >
              {" "}
              ⓘ
            </i>
          </span>
          <input
            type="number"
            step="0.01"
            min="0"
            max="100"
            defaultValue={inputValue(estimate.default_material_pct)}
            onBlur={(e) =>
              void patchEstimate({ default_material_pct: e.target.value || 0 })
            }
          />
        </label>
        <label className="field">
          <span>Overhead %</span>
          <input
            type="number"
            step="0.01"
            defaultValue={inputValue(estimate.overhead_pct)}
            disabled={!estimate.apply_overhead_and_profit}
            onBlur={(e) =>
              void patchEstimate({ overhead_pct: e.target.value || 0 })
            }
          />
        </label>
        <label className="field">
          <span>Profit %</span>
          <input
            type="number"
            step="0.01"
            defaultValue={inputValue(estimate.profit_pct)}
            disabled={!estimate.apply_overhead_and_profit}
            onBlur={(e) =>
              void patchEstimate({ profit_pct: e.target.value || 0 })
            }
          />
        </label>
        <label className="field insure-toggle">
          <span>Claim overhead &amp; profit</span>
          <input
            type="checkbox"
            checked={estimate.apply_overhead_and_profit}
            onChange={(e) =>
              void patchEstimate({
                apply_overhead_and_profit: e.target.checked,
              })
            }
          />
          <i className="na-hint">
            {estimate.apply_overhead_and_profit
              ? "10 and 10, on every line"
              : "Off — no O&P on any line or on the summary"}
          </i>
        </label>
      </div>

      {/* The lines the roof type carries, once there is already work on the
          estimate — see chooseRoofType for why this is a button rather than
          something that happens on its own. */}
      {roofTypeHasTemplate(estimate.roof_type) &&
        view &&
        view.areas.length > 0 && (
          <button
            className="btn-ghost"
            disabled={busy}
            title={`Append the usual ${roofTypeLabel(estimate.roof_type)} scope to ${view.areas[0]!.area.name}`}
            onClick={() =>
              void guard(async () => {
                const added = await applyRoofTemplate(
                  view.areas[0]!.area.id,
                  estimate.roof_type,
                );
                setNote(
                  added
                    ? `${added} ${roofTypeLabel(estimate.roof_type)} line${added === 1 ? "" : "s"} added to ${view.areas[0]!.area.name}. Set the quantities.`
                    : "Nothing to add for this roof type.",
                );
              })
            }
          >
            + Add the standard {roofTypeLabel(estimate.roof_type)} lines
          </button>
        )}
      {note && <p className="na-hint">{note}</p>}

      {/* ------------------------------------------------------ areas ---- */}
      {view?.areas.map(({ area, rows: areaItems, priced, lines }) => {
        return (
          <section key={area.id} className="insure-area">
            <div className="insure-area-head">
              <input
                className="insure-area-name"
                defaultValue={area.name}
                onBlur={(e) =>
                  e.target.value !== area.name &&
                  void guard(() =>
                    updateRow("insurance_estimate_areas", area.id, {
                      name: e.target.value || "Area",
                    }),
                  )
                }
              />
              <button
                className="btn-small danger"
                title="Remove this area and every line in it"
                onClick={() => {
                  if (
                    !window.confirm(
                      `Delete "${area.name}" and its ${areaItems.length} line${
                        areaItems.length === 1 ? "" : "s"
                      }?`,
                    )
                  )
                    return;
                  void guard(() =>
                    deleteRow("insurance_estimate_areas", area.id),
                  );
                }}
              >
                Delete area
              </button>
            </div>

            <div className="ewrap">
              <table className="etable insure-table">
                <thead>
                  <tr>
                    <th className="insure-desc">Description</th>
                    <th className="n">Qty</th>
                    <th>Unit</th>
                    <th className="n">Remove</th>
                    <th className="n">Replace</th>
                    <th className="n">Mat %</th>
                    <th className="n">Tax</th>
                    <th className="n">O&amp;P</th>
                    <th className="n">Total</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {lines.map(({ row: item, line }) => (
                    <tr key={item.id}>
                      <td className="insure-desc">
                        <span className="insure-num">{line.number}.</span>
                        <input
                          className="insure-desc-input"
                          defaultValue={item.description}
                          placeholder="R&R Drip edge"
                          onBlur={(e) =>
                            e.target.value !== item.description &&
                            void patchItem(item, {
                              description: e.target.value,
                            })
                          }
                        />
                        <textarea
                          className="insure-note"
                          rows={item.note ? 2 : 1}
                          defaultValue={item.note ?? ""}
                          placeholder="Code language or scope (printed under the line)"
                          onBlur={(e) =>
                            e.target.value !== (item.note ?? "") &&
                            void patchItem(item, {
                              note: e.target.value || null,
                            })
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="insure-n"
                          type="number"
                          step="0.01"
                          defaultValue={inputValue(item.qty)}
                          onBlur={(e) =>
                            void patchItem(item, { qty: e.target.value || 0 })
                          }
                        />
                      </td>
                      <td>
                        <select
                          className="insure-unit"
                          defaultValue={item.unit}
                          onChange={(e) =>
                            void patchItem(item, { unit: e.target.value })
                          }
                        >
                          {INSURANCE_UNITS.map((unit) => (
                            <option key={unit}>{unit}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          className="insure-n"
                          type="number"
                          step="0.01"
                          defaultValue={inputValue(item.remove_price)}
                          onBlur={(e) =>
                            void patchItem(item, {
                              remove_price: e.target.value || 0,
                            })
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="insure-n"
                          type="number"
                          step="0.01"
                          defaultValue={inputValue(item.replace_price)}
                          onBlur={(e) =>
                            void patchItem(item, {
                              replace_price: e.target.value || 0,
                            })
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="insure-n"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          placeholder={String(estimate.default_material_pct)}
                          defaultValue={inputValue(item.material_pct)}
                          title="Blank uses the estimate's default"
                          onBlur={(e) =>
                            void patchItem(item, {
                              material_pct: e.target.value || null,
                            })
                          }
                        />
                      </td>
                      <td className="n sub">{money(line.tax)}</td>
                      <td className="n sub">{money(line.overheadAndProfit)}</td>
                      <td className="n insure-total">{money(line.total)}</td>
                      <td>
                        <button
                          className="btn-small danger"
                          title="Delete this line"
                          onClick={() =>
                            void guard(() =>
                              deleteRow("insurance_estimate_items", item.id),
                            )
                          }
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                  {areaItems.length === 0 && (
                    <tr>
                      <td colSpan={10} className="sub">
                        No lines in this area yet.
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={6}>Totals: {area.name}</td>
                    <td className="n">{money(priced.tax)}</td>
                    <td className="n">{money(priced.overheadAndProfit)}</td>
                    <td className="n insure-total">{money(priced.total)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>

            <AddLine
              library={library.data ?? []}
              busy={busy}
              onAdd={(row) =>
                void guard(() =>
                  addInsuranceItem(area.id, row, nextSort(areaItems)),
                )
              }
            />
          </section>
        );
      })}

      <button
        className="btn-ghost"
        disabled={busy}
        onClick={() => {
          const name = window.prompt("Name of the area", "Bedroom 1");
          if (!name) return;
          void guard(() =>
            addInsuranceArea(estimate.id, name, nextSort(areas)),
          );
        }}
      >
        + Add area
      </button>

      {/* ---------------------------------------------------- summary ---- */}
      {view && (
        <div className="insure-summary">
          <h3>Summary</h3>
          <table className="insure-sum">
            <tbody>
              <tr>
                <td>Line Item Total</td>
                <td className="n">${money(view.totals.lineItemTotal)}</td>
              </tr>
              <tr>
                <td>Material Sales Tax</td>
                <td className="n">${money(view.totals.materialSalesTax)}</td>
              </tr>
              <tr className="rule">
                <td>Subtotal</td>
                <td className="n">${money(view.totals.subtotal)}</td>
              </tr>
              <tr>
                <td>Overhead</td>
                <td className="n">${money(view.totals.overhead)}</td>
              </tr>
              <tr>
                <td>Profit</td>
                <td className="n">${money(view.totals.profit)}</td>
              </tr>
              <tr className="rule">
                <td>Replacement Cost Value</td>
                <td className="n">
                  ${money(view.totals.replacementCostValue)}
                </td>
              </tr>
              <tr className="claim">
                <td>Net Claim</td>
                <td className="n">${money(view.totals.netClaim)}</td>
              </tr>
            </tbody>
          </table>
          <p className="na-hint">
            This document does not change the job's estimate, its profit or
            anybody's commission — it is the sheet the carrier reads.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Add a line: pick one used before, or start a blank one.
 *
 * The picker is a datalist rather than a modal because adding twenty lines is
 * the common case, and a dialog to dismiss twenty times is a dialog a rep
 * routes around. Choosing a remembered line fills its prices in; typing
 * anything else makes a new line and remembers that instead.
 */
function AddLine({
  library,
  busy,
  onAdd,
}: {
  library: InsuranceLibraryRow[];
  busy: boolean;
  onAdd: (row: Partial<InsuranceItemRow>) => void;
}) {
  const [text, setText] = useState("");

  const add = () => {
    const typed = text.trim();
    // An empty box is not a line. Without this, pressing the button with
    // nothing typed — or a second time, after the first add cleared the field
    // — writes a row with no description, which prints on the adjuster's sheet
    // as a numbered line with 0.00 in every column.
    if (!typed) return;
    const known = library.find(
      (row) => row.description.toLowerCase() === typed.toLowerCase(),
    );
    onAdd(
      known
        ? {
            description: known.description,
            note: known.note,
            unit: known.unit,
            remove_price: known.remove_price,
            replace_price: known.replace_price,
            material_pct: known.material_pct,
          }
        : { description: typed },
    );
    setText("");
  };

  return (
    <div className="insure-add">
      <input
        list="insurance-line-library"
        value={text}
        placeholder="Add a line — start typing, or pick one you have used before"
        disabled={busy}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            add();
          }
        }}
      />
      <datalist id="insurance-line-library">
        {library.map((row) => (
          <option key={row.id} value={row.description} />
        ))}
      </datalist>
      <button className="btn-small" disabled={busy} onClick={add}>
        + Add line
      </button>
    </div>
  );
}
